import {Place} from '@/interfaces/place.interface';
import {GoogleTravelModesEnum} from '@/classes/api/google/GoogleTravelModes.enum';
import {AcquisitionListStatusEnum} from '@/enums/institutioner/acquisition/AcquisitionListStatus.enum';
import {airDistance} from '@/components/acquisitionList/create/acquisitionSearch.helper';

/** What the map search looks for */
export type MapSearchKind = 'institution' | 'employee';

/** Where the search starts: a free address (or the moved pin), a Standort or the home of a teacher */
export type OriginMode = 'address' | 'institution' | 'employee';

/** Travel mode of the route calculation; 'own' = per teacher (car if stored in the profile, otherwise transit) */
export type MapTravelMode = GoogleTravelModesEnum | 'own';

export type MapSortKey = 'air' | 'time' | 'name';

/** Teacher situation of a Standort (assigned wins over planned) */
export type Coverage = 'none' | 'planned' | 'assigned';

/** The institutioner and employee-handler calculate directions for at most this many results per request */
export const EXACT_LIMIT = 50;

export const RADIUS_MIN_KM = 5;
export const RADIUS_MAX_KM = 30;

export interface RouteInfo {
    distance: number;
    duration: number;
    travelMode?: GoogleTravelModesEnum;
}

export interface MapRow {
    _id: string;
    kind: MapSearchKind;
    name: string;
    address?: Place;
    airDistance: number | null;
    /** Standorte; undefined without read access */
    coverage?: Coverage;
    acquisitionStatus?: AcquisitionListStatusEnum | null;
    /** Teachers */
    hasCar?: boolean | null;
    subjects?: string[];
    assignedCount?: number;
    /** Wish of the teacher in meters */
    maxDistance?: number | null;
}

export const COVERAGE: Record<Coverage, { text: string; tone: string; color: string }> = {
    none: {text: 'Keine Lehrkraft', tone: 'tone-warn', color: '#E08A00'},
    planned: {text: 'Vorgemerkt', tone: 'tone-info', color: '#1976D2'},
    assigned: {text: 'Lehrkraft zugeteilt', tone: 'tone-good', color: '#2E7D32'},
};

export const COVERAGE_ITEMS = (Object.keys(COVERAGE) as Coverage[]).map((value) => ({
    value,
    text: value === 'none' ? 'Ohne Lehrkraft' : value === 'planned' ? 'Nur vorgemerkt' : 'Zugeteilt',
}));

/** Marker colors of teachers: with car / without car */
export const CAR_COLOR = '#5E35B1';
export const NO_CAR_COLOR = '#00897B';

export function markerColor(row: MapRow): string {
    if (row.kind === 'institution') {
        return row.coverage ? COVERAGE[row.coverage].color : '#1976D2';
    }
    return row.hasCar === false ? NO_CAR_COLOR : CAR_COLOR;
}

export const MAP_TRAVEL_MODES: Array<{ value: MapTravelMode; text: string; icon: string; noun: string }> = [
    {value: 'own', text: 'Je Lehrkraft', icon: 'mdi-swap-horizontal', noun: 'je Lehrkraft'},
    {value: GoogleTravelModesEnum.DRIVING, text: 'Auto', icon: 'mdi-car', noun: 'mit dem Auto'},
    {value: GoogleTravelModesEnum.TRANSIT, text: 'ÖPNV', icon: 'mdi-bus', noun: 'mit Bus und Bahn'},
    {value: GoogleTravelModesEnum.BICYCLING, text: 'Rad', icon: 'mdi-bicycle', noun: 'mit dem Fahrrad'},
    {value: GoogleTravelModesEnum.WALKING, text: 'Zu Fuß', icon: 'mdi-walk', noun: 'zu Fuß'},
];

/** null in a tri-state filter means "egal" (the filter is not sent) */
export const TRI_STATE = (yes: string, no: string) => [
    {value: null, text: 'Egal'},
    {value: true, text: yes},
    {value: false, text: no},
];

const idOf = (value: any): string => String(value?._id ?? value);

function coverageOf(institution: any): Coverage {
    if (institution.assignedTeachers?.length) {
        return 'assigned';
    }
    return institution.plannedTeachers?.length ? 'planned' : 'none';
}

/** Which optional fields were requested (read access); empty arrays may be missing in the response */
export interface RowAccess {
    coverage?: boolean;
    status?: boolean;
    hasCar?: boolean;
    subjects?: boolean;
    assigned?: boolean;
    maxDistance?: boolean;
}

export function institutionRow(institution: any, origin: Place, access: RowAccess): MapRow {
    return {
        _id: idOf(institution),
        kind: 'institution',
        name: institution.name || 'Ohne Namen',
        address: institution.address,
        airDistance: airDistance(origin, institution.address),
        coverage: access.coverage ? coverageOf(institution) : undefined,
        // a missing status counts as "Offen" (like in the acquisition views)
        acquisitionStatus: access.status ? (institution.acquisitionStatus ?? AcquisitionListStatusEnum.OPEN) : undefined,
    };
}

export function employeeRow(employee: any, origin: Place, access: RowAccess): MapRow {
    const subjects = access.subjects && Array.isArray(employee.subjects)
        ? employee.subjects.map((subject: any) => subject?.name).filter((name: unknown) => typeof name === 'string')
        : undefined;
    return {
        _id: idOf(employee),
        kind: 'employee',
        name: `${employee.firstName || ''} ${employee.lastName || ''}`.trim() || 'Ohne Namen',
        address: employee.address,
        airDistance: airDistance(origin, employee.address),
        hasCar: access.hasCar && typeof employee.hasCar === 'boolean' ? employee.hasCar : null,
        subjects,
        assignedCount: access.assigned ? (employee.assignedInstitutions || []).length : undefined,
        maxDistance: access.maxDistance && typeof employee.maxDistance === 'number' ? employee.maxDistance : null,
    };
}

/** Rows in the chosen order; rows without a value go last */
export function sortRows(rows: MapRow[], sortBy: MapSortKey, routes: Record<string, RouteInfo | null>): MapRow[] {
    const last = Number.MAX_SAFE_INTEGER;
    const result = [...rows];
    switch (sortBy) {
        case 'name':
            return result.sort((a, b) => a.name.localeCompare(b.name, 'de'));
        case 'time':
            return result.sort((a, b) => (routes[a._id]?.duration ?? last) - (routes[b._id]?.duration ?? last)
                || (a.airDistance ?? last) - (b.airDistance ?? last));
        default:
            return result.sort((a, b) => (a.airDistance ?? last) - (b.airDistance ?? last));
    }
}

/** Short one-line address, e.g. "Seestraße 12, 88045 Friedrichshafen" */
export function addressLine(place?: Place | null): string {
    if (!place) {
        return '';
    }
    const street = [place.street, place.houseNumber].filter(Boolean).join(' ');
    const city = [place.postal, place.city].filter(Boolean).join(' ');
    return [street, city].filter(Boolean).join(', ');
}

export function coordinatesOf(place?: Place | null): { lat: number; lng: number } | null {
    const coords = place?.location?.coordinates;
    return coords && coords.length === 2 ? {lat: coords[1], lng: coords[0]} : null;
}

/** Route from the origin in Google Maps (opens in a new tab) */
export function googleRouteUrl(origin: Place | null | undefined, destination: Place | undefined, mode?: GoogleTravelModesEnum): string | null {
    const from = coordinatesOf(origin);
    const to = coordinatesOf(destination);
    if (!from || !to) {
        return null;
    }
    const params = new URLSearchParams({
        api: '1',
        origin: `${from.lat},${from.lng}`,
        destination: `${to.lat},${to.lng}`,
        travelmode: mode || GoogleTravelModesEnum.DRIVING,
    });
    return `https://www.google.com/maps/dir/?${params.toString()}`;
}

/* ---------- Remembered search (V8) ---------- */

/** Settings of the last search per user; never the address or chosen people/Standorte */
export interface MapSearchPreference {
    kind?: MapSearchKind;
    originMode?: OriginMode;
    searchMode?: 'radius' | 'area';
    radiusKm?: number;
    institutionTravelMode?: MapTravelMode;
    employeeTravelMode?: MapTravelMode;
    institutionActive?: boolean | 'all';
    institutionTypes?: number[];
    acquisitionStatus?: number[];
    coverage?: Coverage[];
    employeeActive?: boolean | 'all';
    subjects?: string[];
    hasCar?: boolean | null;
    hasDriverLicense?: boolean | null;
    hasLogin?: boolean | null;
}

const PREFERENCE_PREFIX = 'vanilla.mapSearch.v1';
const ID_PATTERN = /^[a-f0-9]{24}$/i;

function storage(): Storage | null {
    try {
        return typeof window !== 'undefined' && window.localStorage ? window.localStorage : null;
    } catch (e) {
        return null;
    }
}

function preferenceKey(userId: unknown): string | null {
    const id = userId ? String(userId) : '';
    return ID_PATTERN.test(id) ? `${PREFERENCE_PREFIX}.${id}` : null;
}

const oneOf = <T>(value: unknown, allowed: T[]): T | undefined => (allowed.includes(value as T) ? value as T : undefined);
const triState = (value: unknown): boolean | null | undefined => (value === null || typeof value === 'boolean' ? value : undefined);
const activeChoice = (value: unknown): boolean | 'all' | undefined => (typeof value === 'boolean' || value === 'all' ? value : undefined);
const numbers = (value: unknown): number[] | undefined => (Array.isArray(value) ? value.filter((v) => Number.isInteger(v)) : undefined);
const TRAVEL_VALUES: MapTravelMode[] = MAP_TRAVEL_MODES.map((mode) => mode.value);

/** Reads the remembered settings; unknown or invalid values are dropped (the caller still checks access) */
export function readMapSearchPreference(userId: unknown): MapSearchPreference {
    const key = preferenceKey(userId);
    const store = storage();
    if (!key || !store) {
        return {};
    }
    try {
        const raw = JSON.parse(store.getItem(key) || '{}');
        const radius = Number(raw.radiusKm);
        return {
            kind: oneOf<MapSearchKind>(raw.kind, ['institution', 'employee']),
            originMode: oneOf<OriginMode>(raw.originMode, ['address', 'institution', 'employee']),
            searchMode: oneOf<'radius' | 'area'>(raw.searchMode, ['radius', 'area']),
            radiusKm: radius >= RADIUS_MIN_KM && radius <= RADIUS_MAX_KM ? radius : undefined,
            institutionTravelMode: oneOf(raw.institutionTravelMode, TRAVEL_VALUES.filter((mode) => mode !== 'own')),
            employeeTravelMode: oneOf(raw.employeeTravelMode, TRAVEL_VALUES),
            institutionActive: activeChoice(raw.institutionActive),
            institutionTypes: numbers(raw.institutionTypes),
            acquisitionStatus: numbers(raw.acquisitionStatus),
            coverage: Array.isArray(raw.coverage) ? raw.coverage.filter((v: unknown) => v === 'none' || v === 'planned' || v === 'assigned') : undefined,
            employeeActive: activeChoice(raw.employeeActive),
            subjects: Array.isArray(raw.subjects) ? raw.subjects.map(String).filter((id: string) => ID_PATTERN.test(id)) : undefined,
            hasCar: triState(raw.hasCar),
            hasDriverLicense: triState(raw.hasDriverLicense),
            hasLogin: triState(raw.hasLogin),
        };
    } catch (e) {
        return {};
    }
}

export function writeMapSearchPreference(userId: unknown, preference: MapSearchPreference): void {
    const key = preferenceKey(userId);
    const store = storage();
    if (!key || !store) {
        return;
    }
    try {
        store.setItem(key, JSON.stringify(preference));
    } catch (e) {
        // storage full or blocked: the search simply starts with the defaults next time
    }
}
